import { useState } from 'react';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { Button } from '../../../components/Clickables/Buttons/Button';
import { convertWeight } from '@lib/units/convertWeight';

type Tab = 'states' | 'brands';

export interface LeaderboardRow {
  id: string;
  rank: number;
  name: string;
  weightInKg: number;
}

const SKELETON_ROW_COUNT = 10;

const SegmentedControl = ({
  activeTab,
  onChange,
}: {
  activeTab: Tab;
  onChange: (tab: Tab) => void;
}) => (
  <div className="bg-lake-fog flex w-full max-w-81.75 gap-2 rounded-xl p-1">
    {(['states', 'brands'] as const).map(tab => {
      const isActive = activeTab === tab;
      return (
        <button
          key={tab}
          type="button"
          onClick={() => onChange(tab)}
          className={`h-8 flex-1 rounded-lg text-sm font-semibold transition-colors ${
            isActive ? 'bg-deep-sea text-sea-foam' : 'text-abyss'
          }`}
        >
          {tab === 'states' ? 'States' : 'Brands'}
        </button>
      );
    })}
  </div>
);

const LeaderboardHeader = () => (
  <div className="text-abyss flex items-center gap-2 px-2 text-sm font-semibold">
    <p className="w-8">#</p>
    <p className="flex-1">Name</p>
    <p className="text-right whitespace-nowrap">Collected trash</p>
  </div>
);

const LeaderboardRowsSkeleton = () => (
  <div className="flex flex-col">
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <div
        key={index}
        className="border-twilight flex animate-pulse items-center gap-2 border-t px-2 py-3"
      >
        <div className="bg-lake-fog h-4 w-8 rounded" />
        <div className="bg-lake-fog h-4 flex-1 rounded" />
        <div className="bg-lake-fog h-4 w-20 rounded" />
      </div>
    ))}
  </div>
);

const LeaderboardEmptyState = () => (
  <div className="border-twilight flex items-center justify-center border-t px-2 py-6">
    <Text variant="sm" className="text-abyss">
      Leaderboard isn&apos;t available right now.
    </Text>
  </div>
);

const LeaderboardRows = ({ rows }: { rows: LeaderboardRow[] }) => (
  <div className="flex flex-col">
    {rows.map(row => (
      <div
        key={row.id}
        className="border-twilight text-trench flex items-center gap-2 border-t px-2 py-3 text-base"
      >
        <p className="w-8">#{row.rank}</p>
        <p className="flex-1">{row.name}</p>
        <p className="text-right whitespace-nowrap">{convertWeight(row.weightInKg, 'lb')}</p>
      </div>
    ))}
  </div>
);

interface IProps {
  statesRows: LeaderboardRow[];
  isStatesLoading: boolean;
  brandsRows: LeaderboardRow[];
  isBrandsLoading: boolean;
  limit?: number;
}

export const Leaderboard = ({
  statesRows,
  isStatesLoading,
  brandsRows,
  isBrandsLoading,
  limit,
}: IProps) => {
  const [activeTab, setActiveTab] = useState<Tab>('states');
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldSlice = limit !== undefined && !isExpanded;
  const visibleStatesRows = shouldSlice ? statesRows.slice(0, limit) : statesRows;
  const visibleBrandsRows = shouldSlice ? brandsRows.slice(0, limit) : brandsRows;

  const activeTabRowCount = activeTab === 'states' ? statesRows.length : brandsRows.length;
  const canToggle = limit !== undefined && activeTabRowCount > limit;

  const renderBody = () => {
    if (activeTab === 'brands') {
      if (isBrandsLoading) {
        return <LeaderboardRowsSkeleton />;
      }
      if (visibleBrandsRows.length === 0) {
        return <LeaderboardEmptyState />;
      }
      return <LeaderboardRows rows={visibleBrandsRows} />;
    }
    if (isStatesLoading) {
      return <LeaderboardRowsSkeleton />;
    }
    if (visibleStatesRows.length === 0) {
      return <LeaderboardEmptyState />;
    }
    return <LeaderboardRows rows={visibleStatesRows} />;
  };

  return (
    <section className="flex w-full flex-col items-center gap-6">
      <div className="flex w-full flex-col gap-3 px-6">
        <Heading variant="md" as="h2" className="text-trench">
          Leaderboard
        </Heading>
        <Text className="text-abyss">
          The states and brands that picked up the most this season.
        </Text>
      </div>
      <div className="flex w-full justify-center px-6">
        <SegmentedControl activeTab={activeTab} onChange={setActiveTab} />
      </div>
      <div className="w-full px-6">
        <LeaderboardHeader />
        {renderBody()}
      </div>
      {canToggle && (
        <Button variant="controlWhiteBg" size="sm" onClick={() => setIsExpanded(prev => !prev)}>
          {isExpanded ? 'Show less' : 'Show all'}
        </Button>
      )}
    </section>
  );
};
